"use client"

import { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import API from "../services/api"
import "./PageContentsPage.css"
import CompressedFileInput from "../components/CompressedFileInput"
import RichTextEditor from "../components/RichTextEditor"
import ConfirmDialog from "../components/ConfirmDialog"
import Toast, { useToast } from "../components/Toast"

const PageContentsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [pageContents, setPageContents] = useState([])
  const [formData, setFormData] = useState({
    pageName: "",
    type: "default",
    description: "",
    images: [],
    pdf: null,
    video: null,
    translations: {
      fr: { description: "" },
      ar: { description: "" },
      en: { description: "" },
    },
  })

  const [previewImages, setPreviewImages] = useState([])
  const [previewPdf, setPreviewPdf] = useState(null)
  const [existingImages, setExistingImages] = useState([])
  const [newImages, setNewImages] = useState([])
  const [existingVideo, setExistingVideo] = useState("")
  const [videoObjectUrl, setVideoObjectUrl] = useState(null)

  const [editId, setEditId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [user, setUser] = useState({ username: "", email: "" })
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [searchTerm, setSearchTerm] = useState("")
  const [sortOrder, setSortOrder] = useState("newest")
  const [viewMode, setViewMode] = useState("grid")
  const [currentLang, setCurrentLang] = useState("fr")
  const [toasts, setToasts] = useState([])
  const { addToast, removeToast } = useToast(setToasts)
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: "", onConfirm: null })
const pageLabels = {
  Livret: "Livret d'accueil",
  IbisMusic: "Ibis Music",
  PlanSfax: "Se déplacer",
  PlanTunis: "Se déplacer",
  Presse: "Presse",
  Boissons: "Bar",
  Restaurants: "Restaurant",
  PetitDejeuner: "Petit Déjeuner",
  EnCas: "En-cas",
  ServicesHotel: "Services hôtel",
  Coworking: "Co-working",
  Contact: "Contact",
}
  // Theme state with localStorage initialization
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme")
    return savedTheme ? savedTheme === "dark" : true
  })

  const fetchPageContents = async () => {
    try {
      setIsLoading(true)
      const res = await API.get("/page-contents")
      setPageContents(res.data)
    } catch (error) {
      console.error("Erreur chargement contenus de page:", error)
      alert("Erreur de chargement")
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      message: "Êtes-vous sûr de vouloir supprimer ce contenu de page ?",
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: "", onConfirm: null })
        try {
          setIsLoading(true)
          await API.delete(`/page-contents/${id}`)
          addToast("Contenu de page supprimé avec succès", "success")
          fetchPageContents()
        } catch (error) {
          console.error("Erreur suppression contenu de page:", error)
          addToast("Erreur lors de la suppression", "error")
        } finally {
          setIsLoading(false)
        }
      },
    })
  }

  useEffect(() => {
    const token = localStorage.getItem("accessToken")
    if (!token) navigate("/")

    const userData = JSON.parse(localStorage.getItem("user") || '{"username":"test","email":"test@gmail.com"}')
    setUser(userData)

    const loadTimer = setTimeout(() => setIsLoading(false), 800)
    const timer = setInterval(() => setCurrentTime(new Date()), 60000)

    fetchPageContents()

    return () => {
      clearTimeout(loadTimer)
      clearInterval(timer)
    }
  }, [navigate])

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem("theme", isDarkMode ? "dark" : "light")
  }, [isDarkMode])

  useEffect(() => {
    const q = location.state?.pageContentsSearch
    if (typeof q === "string" && q.trim()) {
      setSearchTerm(q.trim())
    }
  }, [location.state])

  useEffect(() => {
    return () => {
      if (videoObjectUrl) URL.revokeObjectURL(videoObjectUrl)
    }
  }, [videoObjectUrl])

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode)
  }

  const handleChange = (e) => {
    const { name, value, files } = e.target

    if (name === "images" && files && files.length > 0) {
      // Handle multiple images - append all to existing images
      const imageFiles = Array.from(files)
      imageFiles.forEach((imageFile) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          setNewImages((prev) => [...prev, imageFile])
          setFormData((prev) => ({
            ...prev,
            images: [...prev.images, imageFile],
            pdf: null,
          }))
          setPreviewImages((prev) => [...prev, { src: reader.result, isNew: true }])
        }
        reader.readAsDataURL(imageFile)
      })
      setPreviewPdf(null)
    } else if (name === "video" && files && files[0]) {
      const videoFile = files[0]
      setVideoObjectUrl((prevUrl) => {
        if (prevUrl) URL.revokeObjectURL(prevUrl)
        return URL.createObjectURL(videoFile)
      })
      setExistingVideo("")
      setFormData((prev) => ({ ...prev, video: videoFile, pdf: null }))
    } else if (name === "pdf" && files && files[0]) {
      // Handle single PDF
      const pdfFile = files[0]
      setVideoObjectUrl((prevUrl) => {
        if (prevUrl) URL.revokeObjectURL(prevUrl)
        return null
      })
      setExistingVideo("")
      setFormData((prev) => ({ ...prev, pdf: pdfFile, images: [], video: null }))
      setPreviewPdf(pdfFile.name)
      setPreviewImages([])
      setExistingImages([])
      setNewImages([])
    } else if (name === "type") {
      // Reset files when type changes
      setVideoObjectUrl((prevUrl) => {
        if (prevUrl) URL.revokeObjectURL(prevUrl)
        return null
      })
      setExistingVideo("")
      setFormData((prev) => ({ ...prev, type: value, images: [], pdf: null, video: null }))
      setPreviewImages([])
      setPreviewPdf(null)
      setExistingImages([])
      setNewImages([])
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const updateTranslationField = (lang, value) => {
    setFormData((prev) => {
      const safeTranslations = prev.translations || {
        fr: { description: "" },
        ar: { description: "" },
        en: { description: "" },
      }

      const updatedTranslations = {
        ...safeTranslations,
        [lang]: {
          ...(safeTranslations[lang] || { description: "" }),
          description: value,
        },
      }

      return {
        ...prev,
        translations: updatedTranslations,
        ...(lang === "fr" ? { description: value } : {}),
      }
    })
  }

const removeImage = (index) => {
  const image = previewImages[index];

  if (image.isNew) {
    // Remove from new images
    const newImageIndex = previewImages
      .slice(0, index)
      .filter((img) => img.isNew).length;

    setNewImages((prev) => prev.filter((_, i) => i !== newImageIndex));
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== newImageIndex),
    }));
  } else {
    // Remove from existing images - FIXED VERSION
    const existingImageIndex = previewImages
      .slice(0, index)
      .filter((img) => !img.isNew).length;

    setExistingImages((prev) => {
      const updated = [...prev];
      updated.splice(existingImageIndex, 1);
      console.log("🗑️ Updated existingImages:", updated);
      return updated;
    });
  }

  // Update previewImages
  setPreviewImages((prev) => prev.filter((_, i) => i !== index));
};

  const removeVideo = () => {
    setVideoObjectUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl)
      return null
    })
    setExistingVideo("")
    setFormData((prev) => ({ ...prev, video: null }))
  }

const handleSubmit = async (e) => {
  e.preventDefault()
  setIsLoading(true)

  try {
    const form = new FormData()
    form.append("pageName", formData.pageName)
    form.append(
      "description",
      formData.translations?.fr?.description || formData.description || "",
    )
    form.append("type", formData.type || "default")
    form.append("translations", JSON.stringify(formData.translations || {}))

    if (formData.type === "pdf" && formData.pdf) {
      // Append single PDF file
      form.append("pdf", formData.pdf)
      console.log("📄 Appending PDF:", formData.pdf.name)
    } else if (formData.type === "default" || formData.type === "mixed") {
      // ✅ Always send existingImages, even if empty
      form.append("existingImages", JSON.stringify(existingImages))
      console.log("📸 Existing images to keep:", existingImages)

      // Append only new images
      newImages.forEach((img, index) => {
        form.append("images", img)
        console.log(`🖼️ Appending new image ${index + 1}:`, img.name)
      })
    }

    if (formData.type === "video" || formData.type === "mixed") {
      form.append("existingVideo", JSON.stringify(existingVideo || ""))
      if (formData.video) {
        form.append("video", formData.video)
        console.log("🎬 Appending video:", formData.video.name)
      }
    }

    console.log("📤 FormData being sent:")
    for (const pair of form.entries()) {
      console.log(pair[0], pair[1])
    }

    if (editId) {
      await API.put(`/page-contents/${editId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      addToast("Contenu de page modifié avec succès", "success")
    } else {
      await API.post("/page-contents", form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      addToast("Contenu de page créé avec succès", "success")
    }

    fetchPageContents()
    resetForm()
  } catch (error) {
    console.error("Erreur enregistrement:", error)
    addToast("Erreur lors de l'enregistrement: " + (error.response?.data?.message || error.message), "error")
  } finally {
    setIsLoading(false)
  }
}

  const handleEdit = (pageContent) => {
    setFormData({
      pageName: pageContent.pageName,
      description: pageContent.description || pageContent.translations?.fr?.description || "",
      type: pageContent.type || "default",
      images: [],
      pdf: null,
      video: null,
      translations: pageContent.translations || {
        fr: {
          description: pageContent.translations?.fr?.description || pageContent.description || "",
        },
        ar: {
          description: pageContent.translations?.ar?.description || "",
        },
        en: {
          description: pageContent.translations?.en?.description || "",
        },
      },
    })

    setVideoObjectUrl(null)
    setExistingVideo("")

    if (pageContent.type === "pdf" && pageContent.pdf) {
      setPreviewPdf(pageContent.pdf)
      setPreviewImages([])
      setExistingImages([])
      setNewImages([])
    } else if (pageContent.type !== "pdf") {
      if (pageContent.images && pageContent.images.length > 0) {
        setExistingImages(pageContent.images)
        setPreviewImages(pageContent.images.map((img) => ({ src: img, isNew: false })))
        setNewImages([])
      } else {
        setPreviewImages([])
        setExistingImages([])
        setNewImages([])
      }
      setPreviewPdf(null)
      if (pageContent.video) {
        setExistingVideo(pageContent.video)
      }
    }

    setEditId(pageContent._id)
    setShowForm(true)
    setCurrentLang("fr")
  }

  const resetForm = () => {
    setFormData({
      pageName: "",
      description: "",
      type: "default",
      images: [],
      pdf: null,
      video: null,
      translations: {
        fr: { description: "" },
        ar: { description: "" },
        en: { description: "" },
      },
    })
    setPreviewImages([])
    setPreviewPdf(null)
    setExistingImages([])
    setNewImages([])
    removeVideo()
    setEditId(null)
    setShowForm(false)
  }

  const formatDate = (date) => {
    const options = { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    return date.toLocaleDateString("fr-FR", options)
  }

  const formatTime = (date) => {
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const filteredPageContents = pageContents
    .filter(
      (pc) =>
        pc.pageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pc.description?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      if (sortOrder === "newest") {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      } else if (sortOrder === "oldest") {
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      } else if (sortOrder === "alphabetical") {
        return a.pageName.localeCompare(b.pageName)
      }
      return 0
    })

  if (isLoading) {
    return (
      <div className={isDarkMode ? "loading-container" : "light-loading-container"}>
        <div className={isDarkMode ? "loading-logo" : "light-loading-logo"}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"} 
    alt="Guestly Logo"
    className="logo-image"
  />
</div>
        <div className={isDarkMode ? "loading-spinner" : "light-loading-spinner"}>
          <div className={isDarkMode ? "spinner-circle" : "light-spinner-circle"}></div>
          <div className={isDarkMode ? "spinner-circle-inner" : "light-spinner-circle-inner"}></div>
        </div>
        <div className={isDarkMode ? "loading-text" : "light-loading-text"}>Chargement...</div>
      </div>
    )
  }

  return (
    <div className={isDarkMode ? "dashboard" : "light-dashboard"}>
      <div className={isDarkMode ? "mobile-header" : "light-mobile-header"}>
        <button className={isDarkMode ? "menu-toggle" : "light-menu-toggle"} onClick={toggleSidebar}>
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className={isDarkMode ? "mobile-logo" : "light-mobile-logo"}>
          <span style={{ display: "inline-block", verticalAlign: "middle" }}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
    alt="Guestly Logo"
    style={{
      width: "180px",        // wide logo
      height: "auto",        // maintain aspect ratio
      objectFit: "contain",
      transition: "opacity 0.3s ease"
    }}
  />
</span>

        </div>
        <div className={isDarkMode ? "mobile-user" : "light-mobile-user"}>
          <div className={isDarkMode ? "user-avatar" : "light-user-avatar"}>{user.username.charAt(0)}</div>
        </div>
      </div>

      <div className={`${isDarkMode ? "sidebar" : "light-sidebar"} ${isSidebarOpen ? "open" : ""}`}>
        <div className={isDarkMode ? "sidebar-header" : "light-sidebar-header"}>
          <div className={isDarkMode ? "logo" : "light-logo"}>
            <span style={{ display: "inline-block", verticalAlign: "middle" }}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
    alt="Guestly Logo"
    style={{
      width: "180px",        // wide logo
      height: "auto",        // maintain aspect ratio
      objectFit: "contain",
      transition: "opacity 0.3s ease"
    }}
  />
</span>

          </div>
          <button className={isDarkMode ? "close-sidebar" : "light-close-sidebar"} onClick={toggleSidebar}>
            ×
          </button>
        </div>

        <div className={isDarkMode ? "user-profile" : "light-user-profile"}>
          <div className={isDarkMode ? "user-avatar" : "light-user-avatar"}>{user.username.charAt(0)}</div>
          <div className={isDarkMode ? "user-info" : "light-user-info"}>
            <div className={isDarkMode ? "user-name" : "light-user-name"}>{user.username}</div>
            <div className={isDarkMode ? "user-email" : "light-user-email"}>{user.email}</div>
          </div>
        </div>

        <div className={isDarkMode ? "sidebar-date" : "light-sidebar-date"}>
          <div className={isDarkMode ? "date-display" : "light-date-display"}>{formatDate(currentTime)}</div>
          <div className={isDarkMode ? "time-display" : "light-time-display"}>{formatTime(currentTime)}</div>
        </div>

        <nav className={isDarkMode ? "sidebar-nav" : "light-sidebar-nav"}>
          <ul>
            <li className="active">
              <a href="/dashboard">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🏠</span>
                <span>Tableau de bord</span>
              </a>
            </li>
            <li>
              <a href="/profile">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>👤</span>
                <span>Mon profil</span>
              </a>
            </li>
            <li>
              <a href="/statistiques">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>📊</span>
                <span>Statistiques</span>
              </a>
            </li>
            <li>
              <a href="/historique">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>📋</span>
                <span>Historique</span>
              </a>
            </li>
            <li>
              <a href="/settings">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>⚙️</span>
                <span>Paramètres</span>
              </a>
            </li>
          </ul>
        </nav>

        <div className={isDarkMode ? "sidebar-footer" : "light-sidebar-footer"}>
          <button
            className={isDarkMode ? "logout-button" : "light-logout-button"}
            onClick={() => {
              localStorage.removeItem("accessToken")
              localStorage.removeItem("user")
              navigate("/")
            }}
          >
            <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🚪</span>
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      <div className={isDarkMode ? "main-content" : "light-main-content"}>
        <div className={isDarkMode ? "welcome-section" : "light-welcome-section"}>
          <div className={isDarkMode ? "welcome-header" : "light-welcome-header"}>
            <h1>
              Contenus de Pages <span className={isDarkMode ? "wave-emoji" : "light-wave-emoji"}>📄</span>
            </h1>
            <div className={isDarkMode ? "welcome-actions" : "light-welcome-actions"}>
              <button
                className={isDarkMode ? "theme-toggle-button" : "light-theme-toggle-button"}
                onClick={toggleTheme}
                title={isDarkMode ? "Passer en mode clair" : "Passer en mode sombre"}
              >
                {isDarkMode ? "☀️" : "🌙"}
              </button>

              <div className={isDarkMode ? "user-avatar-small" : "light-user-avatar-small"}>
                {user.username.charAt(0)}
              </div>
            </div>
          </div>
          <p>Gérez le contenu de vos différentes pages</p>
        </div>

        <div className={isDarkMode ? "search-filter-container" : "light-search-filter-container"}>
          <div className={isDarkMode ? "search-container" : "light-search-container"}>
            <span className="search-icon">🔍</span>
            <input
              className={isDarkMode ? "search-input" : "light-search-input"}
              type="text"
              placeholder="Rechercher un contenu de page..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="view-sort-container">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label="Vue en grille"
              >
                ▦
              </button>
              <button
                className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label="Vue en liste"
              >
                ☰
              </button>
            </div>

            <select
              className={isDarkMode ? "sort-select" : "light-sort-select"}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="newest">Plus récent</option>
              <option value="oldest">Plus ancien</option>
              <option value="alphabetical">Alphabétique</option>
            </select>
          </div>
        </div>

        <div className={isDarkMode ? "section-header" : "light-section-header"}>
          <h2>Liste des contenus de pages</h2>
          <div className="section-actions">
            <button
              className={isDarkMode ? "section-action" : "light-section-action"}
              onClick={() => setShowForm(!showForm)}
            >
              <span>{showForm ? "Annuler" : "Ajouter"}</span>
              <span className="action-icon">{showForm ? "❌" : "+"}</span>
            </button>
          </div>
        </div>

        {showForm && (
          <div className="pc-form-container">
            <div className="pc-form-header">
              <h3 className="pc-form-title">
                {editId ? "✏️ Modifier le contenu" : "➕ Nouveau contenu de page"}
              </h3>
              <button type="button" className="pc-form-close" onClick={resetForm} title="Fermer">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="pc-form">
              {/* Row 1 : page + type */}
              <div className="pc-form-row">
                <div className="pc-field">
                  <label className="pc-label">Page cible</label>
                  <select
                    name="pageName"
                    value={formData.pageName}
                    onChange={handleChange}
                    required
                    className="pc-select"
                  >
                    <option value="">— Sélectionner —</option>
                    <option value="Livret">Livret d'accueil</option>
                    <option value="IbisMusic">Ibis Music</option>
                    <option value="PlanTunis">Se déplacer</option>
                    <option value="Presse">Presse</option>
                    <option value="Boissons">Bar</option>
                    <option value="Restaurants">Restaurant</option>
                    <option value="PetitDejeuner">Petit Déjeuner</option>
                    <option value="EnCas">En-cas</option>
                    <option value="ServicesHotel">Services hôtel</option>
                    <option value="AdditionalService">Additional Service</option>
                    <option value="Coworking">Co-working</option>
                    <option value="Contact">Contact</option>
                  </select>
                </div>

                <div className="pc-field">
                  <label className="pc-label">Type de contenu</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="pc-select"
                  >
                    <option value="default">🖼️ Texte + Images</option>
                    <option value="video">🎬 Texte + Vidéo</option>
                    <option value="mixed">🖼️🎬 Texte + Images + Vidéo</option>
                    <option value="pdf">📄 PDF</option>
                  </select>
                </div>
              </div>

              {formData.type !== "pdf" ? (
                <>
                  {/* Description section */}
                  <div className="pc-section-block">
                    <div className="pc-section-label">
                      <span>Description</span>
                      <div className="pc-lang-tabs">
                        {["fr", "ar", "en"].map((lang) => (
                          <button
                            key={lang}
                            type="button"
                            className={`pc-lang-tab ${currentLang === lang ? "active" : ""}`}
                            onClick={() => setCurrentLang(lang)}
                          >
                            {lang === "fr" ? "🇫🇷" : lang === "ar" ? "🇸🇦" : "🇬🇧"} {lang.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                    <RichTextEditor
                      key={currentLang}
                      content={formData.translations?.[currentLang]?.description || ""}
                      onChange={(html) => updateTranslationField(currentLang, html)}
                      isDarkMode={isDarkMode}
                    />
                  </div>

                  {/* Images section */}
                  {(formData.type === "default" || formData.type === "mixed") && (
                    <div className="pc-section-block">
                      <div className="pc-section-label">
                        <span>🖼️ Images</span>
                        {previewImages.length > 0 && (
                          <span style={{ fontSize: "13px", color: "#888", marginLeft: "8px" }}>
                            ({previewImages.length} image{previewImages.length > 1 ? "s" : ""})
                          </span>
                        )}
                      </div>
                      <CompressedFileInput
                        type="file"
                        name="images"
                        accept="image/*"
                        multiple
                        maxTotalSizeMB={20}
                        onChange={handleChange}
                        className="pc-file-input"
                      />
                      {previewImages.length > 0 && (
                        <div className="pc-preview-grid">
                          {previewImages.map((preview, index) => (
                            <div key={index} className="pc-preview-item">
                              <img
                                src={preview.src || preview || "/placeholder.svg"}
                                alt={`Aperçu ${index + 1}`}
                                className="pc-preview-img"
                              />
                              {!preview.isNew && <span className="pc-badge-existing">Existant</span>}
                              <button
                                type="button"
                                className="pc-remove-btn"
                                onClick={() => removeImage(index)}
                                title="Supprimer"
                              >✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video section */}
                  {(formData.type === "video" || formData.type === "mixed") && (
                    <div className="pc-section-block">
                      <div className="pc-section-label"><span>🎬 Vidéo</span></div>
                      <input
                        type="file"
                        name="video"
                        accept="video/*"
                        onChange={handleChange}
                        className="pc-file-input"
                      />
                      {(videoObjectUrl || existingVideo) && (
                        <div className="pc-video-wrap">
                          <video
                            key={videoObjectUrl || existingVideo}
                            src={videoObjectUrl || existingVideo}
                            controls muted playsInline
                            className="pc-video-player"
                          />
                          {!formData.video && existingVideo && (
                            <span className="pc-badge-existing">Existant</span>
                          )}
                          <button type="button" className="pc-remove-btn" onClick={removeVideo} title="Supprimer">✕</button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="pc-section-block">
                  <div className="pc-section-label"><span>📄 Fichier PDF</span></div>
                  <input
                    type="file"
                    name="pdf"
                    accept="application/pdf"
                    onChange={handleChange}
                    className="pc-file-input"
                  />
                  {previewPdf && (
                    <div className="pc-pdf-preview">
                      📄 {typeof previewPdf === "string" ? previewPdf.split("/").pop() : previewPdf}
                    </div>
                  )}
                </div>
              )}

              <div className="pc-form-actions">
                <button type="submit" className="pc-btn pc-btn-primary" disabled={isLoading}>
                  {isLoading ? "⏳ Enregistrement…" : editId ? "✅ Enregistrer les modifications" : "✅ Créer le contenu"}
                </button>
                <button type="button" className="pc-btn pc-btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {filteredPageContents.length === 0 ? (
          <div className={isDarkMode ? "empty-state" : "light-empty-state"}>
            <div className="pc-empty-icon">📄</div>
            <h3>Aucun contenu de page trouvé</h3>
            <p>Commencez par créer un nouveau contenu de page</p>
            <button
              className="pc-btn pc-btn-primary"
              onClick={() => setShowForm(true)}
            >
              ➕ Créer un contenu
            </button>
          </div>
        ) : (
          <div className={`pc-cards-grid ${viewMode === "list" ? "list-view" : ""}`}>
            {filteredPageContents.map((pageContent) => {
              const typeLabel =
                pageContent.type === "pdf" ? "📄 PDF"
                : pageContent.type === "video" ? "🎬 Vidéo"
                : pageContent.type === "mixed" ? "🖼️🎬 Images + Vidéo"
                : "🖼️ Images"
              const typeColor =
                pageContent.type === "pdf" ? "#f97316"
                : pageContent.type === "video" ? "#8b5cf6"
                : pageContent.type === "mixed" ? "#06b6d4"
                : "#ec4899"
              const hasMedia = (pageContent.images?.length > 0) || pageContent.video

              return (
                <div key={pageContent._id} className="pc-card">
                  {/* Media strip */}
                  {pageContent.type === "pdf" && pageContent.pdf ? (
                    <div className="pc-card-pdf-strip">
                      <span className="pc-card-pdf-icon">📄</span>
                      <a href={pageContent.pdf} target="_blank" rel="noopener noreferrer" className="pc-card-pdf-link">
                        Ouvrir le PDF ↗
                      </a>
                    </div>
                  ) : hasMedia ? (
                    <div className="pc-card-media">
                      {pageContent.video && (
                        <video
                          src={pageContent.video}
                          className="pc-card-media-video"
                          muted playsInline preload="metadata"
                        />
                      )}
                      {pageContent.images?.length > 0 && (
                        <div className="pc-card-media-images">
                          {pageContent.images.slice(0, 3).map((img, i) => (
                            <img
                              key={i}
                              src={img || "/placeholder.svg"}
                              alt=""
                              className="pc-card-thumb"
                            />
                          ))}
                          {pageContent.images.length > 3 && (
                            <div className="pc-card-thumb-more">+{pageContent.images.length - 3}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="pc-card-no-media">Aucun média</div>
                  )}

                  {/* Card body */}
                  <div className="pc-card-body">
                    <div className="pc-card-top">
                      <h3 className="pc-card-title">
                        {pageLabels[pageContent.pageName] || pageContent.pageName}
                      </h3>
                      <span className="pc-card-type-badge" style={{ background: typeColor }}>
                        {typeLabel}
                      </span>
                    </div>

                    {(pageContent.translations?.fr?.description || pageContent.description) && (
                      <div
                        className="pc-card-description"
                        dangerouslySetInnerHTML={{
                          __html: pageContent.translations?.fr?.description || pageContent.description,
                        }}
                      />
                    )}

                    <div className="pc-card-meta">
                      <span>📅 {new Date(pageContent.createdAt).toLocaleDateString("fr-FR")}</span>
                      <span>🔄 {new Date(pageContent.updatedAt).toLocaleDateString("fr-FR")}</span>
                    </div>

                    <div className="pc-card-actions">
                      <button className="pc-btn pc-btn-secondary" onClick={() => handleEdit(pageContent)}>
                        ✏️ Modifier
                      </button>
                      <button className="pc-btn pc-btn-danger" onClick={() => handleDelete(pageContent._id)}>
                        🗑️ Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>



      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        isDarkMode={isDarkMode}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, message: "", onConfirm: null })}
      />
      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  )
}

export default PageContentsPage
